import React from "react";
import { format } from "date-fns";
import { get } from "lodash";
import { makeStyles } from "helpers/StyleHelper";
import { TableCell, TableCellProps, Theme, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import type { TableCellType, BooleanCellType, NodeCellType } from "./index";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      width: "auto",
      boxShadow: "none",
    },
  },
  dateCell: {
    whiteSpace: "nowrap",
  },
}));

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const BasicCell = ({
  cellProps,
  value,
}: {
  cellProps: TableCellProps;
  value: string;
}) => <TableCell {...cellProps}>{value}</TableCell>;

const BooleanCell = ({
  cellProps,
  value,
  TrueComponent,
}: {
  cellProps: TableCellProps;
  value: boolean;
  TrueComponent?: React.ReactNode;
}) => (
  <TableCell {...cellProps}>
    {value ? TrueComponent ?? <CheckIcon /> : null}
  </TableCell>
);

const DateCell = ({
  cellProps,
  value,
  className,
}: {
  cellProps: TableCellProps;
  value: Date;
  className?: string;
}) => (
  <TableCell {...cellProps} className={className}>
    {value ? format(value, "P") : null}
  </TableCell>
);

const DateTimeCell = ({
  cellProps,
  value,
  className,
}: {
  cellProps: TableCellProps;
  value: Date;
  className?: string;
}) => (
  <TableCell {...cellProps} className={className}>
    {value ? format(value, "Pp") : null}
  </TableCell>
);

const PriceCell = ({
  cellProps,
  value,
  className,
}: {
  cellProps: TableCellProps;
  value: number;
  className?: string;
}) => (
  <TableCell {...cellProps} className={className}>
    {value ? priceFormatter.format(value) : null}
  </TableCell>
);

type EnhancedTableCellProps = {
  cell: TableCellType;
  row: object;
};

const EnhancedTableCell = ({ cell, row }: EnhancedTableCellProps) => {
  const styles = useStyles(useTheme());
  const cellProps = {
    className: cell.className,
    align: cell.align,
  } as TableCellProps;
  const value = get(row, cell.accessor);

  switch (cell.type) {
    case "boolean":
      return (
        <BooleanCell
          cellProps={cellProps}
          TrueComponent={(cell as BooleanCellType).TrueComponent}
          value={value}
        />
      );
    case "date":
      return (
        <DateCell
          cellProps={{ ...cellProps, sx: styles.dateCell }}
          className={cell.className}
          value={value}
        />
      );
    case "datetime":
      return (
        <DateTimeCell
          cellProps={{ ...cellProps, sx: styles.dateCell }}
          className={cell.className}
          value={value}
        />
      );
    case "node": {
      const View = (cell as NodeCellType).View;
      return (
        <TableCell {...cellProps}>
          <View value={row} />
        </TableCell>
      );
    }
    case "price":
      return <PriceCell cellProps={cellProps} value={value} />;
    default:
      return <BasicCell cellProps={cellProps} value={value} />;
  }
};

export default EnhancedTableCell;
